<script setup>
// The app's one confirmation dialog (see lib/confirm.js). An alertdialog:
// focus moves in (to the safe choice, or the field to type into), stays in
// (Tab cycles), Escape cancels, and focus returns to where it was.
import { ref, computed, watch, nextTick } from "vue"
import { AlertTriangle, HelpCircle, Copy } from "lucide-vue-next"
import Button from "@/components/ui/Button.vue"
import Input from "@/components/ui/Input.vue"
import { confirmState } from "@/lib/confirm"

const box = ref(null)
const typed = ref("")
let returnFocus = null

const s = computed(() => confirmState.value)
const canConfirm = computed(() => !s.value?.requireText || typed.value.trim() === s.value.requireText)

watch(s, async (now, before) => {
  if (now && !before) returnFocus = document.activeElement
  typed.value = ""
  if (!now) {
    returnFocus?.focus?.({ preventScroll: true }); returnFocus = null
    return
  }
  await nextTick()
  const el = box.value
  if (!el) return
  if (now.kind === "copy") { const i = el.querySelector("input"); i?.focus(); i?.select(); return }
  // The field to type into, else the safe choice: Enter must not confirm by accident.
  ;(el.querySelector("input") || el.querySelector("[data-cancel]"))?.focus()
})

const cancel = () => s.value?.resolve(false)
const confirm = () => { if (canConfirm.value) s.value?.resolve(true) }

// Keep Tab inside the dialog.
const onKeydown = (e) => {
  if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); cancel(); return }
  if (e.key !== "Tab") return
  const items = [...box.value.querySelectorAll("button:not([disabled]), input")]
  if (!items.length) return
  const first = items[0], last = items[items.length - 1]
  if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus() }
  else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus() }
}
</script>

<template>
  <Teleport to="body">
    <div v-if="s" class="fixed inset-0 z-[70] flex items-end justify-center bg-black/40 p-4 sm:items-center" @mousedown.self="cancel">
      <div ref="box" role="alertdialog" aria-modal="true" aria-labelledby="confirm-title"
        :aria-describedby="s.message ? 'confirm-message' : undefined" @keydown="onKeydown"
        class="w-full max-w-md rounded-2xl border bg-card p-5 shadow-2xl">
        <div class="flex items-start gap-3">
          <div class="grid h-9 w-9 shrink-0 place-items-center rounded-full"
            :class="s.danger ? 'bg-destructive/10 text-destructive' : 'bg-primary/10 text-primary'" aria-hidden="true">
            <component :is="s.kind === 'copy' ? Copy : s.danger ? AlertTriangle : HelpCircle" class="h-5 w-5" />
          </div>
          <div class="min-w-0 flex-1">
            <h2 id="confirm-title" class="font-semibold leading-snug">{{ s.title }}</h2>
            <p v-if="s.message" id="confirm-message" class="mt-1 whitespace-pre-line text-sm text-muted-foreground">{{ s.message }}</p>
          </div>
        </div>

        <template v-if="s.kind === 'copy'">
          <input :value="s.text" readonly aria-label="Текст для копирования"
            class="mt-4 w-full rounded-md border border-input bg-muted px-3 py-2 font-mono text-xs" @focus="$event.target.select()" />
          <div class="mt-4 flex justify-end">
            <Button data-cancel @click="cancel">Готово</Button>
          </div>
        </template>

        <template v-else>
          <div v-if="s.requireText" class="mt-4 space-y-1.5">
            <label for="confirm-type" class="text-sm">Чтобы подтвердить, введите <strong class="font-mono">{{ s.requireText }}</strong></label>
            <Input id="confirm-type" v-model="typed" autocomplete="off" @keydown.enter.prevent="confirm" />
          </div>
          <div class="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button data-cancel variant="outline" @click="cancel">{{ s.cancelLabel }}</Button>
            <Button :class="s.danger ? 'bg-destructive text-white hover:bg-destructive/90' : ''" :disabled="!canConfirm" @click="confirm">
              {{ s.confirmLabel }}
            </Button>
          </div>
        </template>
      </div>
    </div>
  </Teleport>
</template>
